import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Cookie, ArrowLeft } from "lucide-react";

// Simple public layout for policy pages
function PublicLayout({ children }) {
	return (
		<div className="theme-dark-scope min-h-screen bg-obsidian">
			<header className="glass-header sticky top-0 z-50">
				<div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
					<Link to="/" className="flex items-center gap-3">
						<img
							src="/logo-kraveon.svg"
							alt="Kraveon"
							className="w-9 h-9 rounded-lg"
						/>
						<span className="font-heading text-lg tracking-tight">KRAVEON</span>
					</Link>
					<Link
						to="/login"
						className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
					>
						<ArrowLeft className="w-4 h-4" />
						Torna al login
					</Link>
				</div>
			</header>
			<main className="pb-20">{children}</main>
			<footer className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-sm border-t border-border py-2 z-30">
				<div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs text-muted-foreground">
					<span>© 2026 Kraveon</span>
					<div className="flex items-center gap-4">
						<Link
							to="/privacy-policy"
							className="hover:text-volt transition-colors"
						>
							Privacy Policy
						</Link>
						<Link
							to="/cookie-policy"
							className="hover:text-volt transition-colors"
						>
							Cookie Policy
						</Link>
						<button
							onClick={() => window.openCookieSettings?.()}
							className="hover:text-volt transition-colors"
						>
							Gestisci Cookie
						</button>
					</div>
				</div>
			</footer>
		</div>
	);
}

export default function CookiePolicy() {
	const { language } = useLanguage();

	const content =
		language === "it"
			? {
					title: "Cookie Policy",
					lastUpdate: "Ultimo aggiornamento: Ottobre 2026",
					intro: `Questa Cookie Policy spiega cosa sono i cookie, come li utilizziamo su Kraveon e come gestire le tue preferenze. La presente policy è redatta in conformità al Regolamento UE 2016/679 (GDPR), alla Direttiva 2002/58/CE (ePrivacy), al D.Lgs. 196/2003 (Codice Privacy) come modificato dal D.Lgs. 101/2018, e alle Linee Guida del Garante per la protezione dei dati personali del 10 giugno 2021.`,
					sections: [
						{
							title: "1. Cosa sono i Cookie",
							content: `I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo quando visiti un sito web. Sono ampiamente utilizzati per far funzionare i siti web in modo più efficiente e per fornire informazioni ai proprietari del sito.

I cookie possono essere:
• Cookie di prima parte: impostati direttamente dal nostro sito
• Cookie di terze parti: impostati da domini diversi dal nostro

In base alla durata:
• Cookie di sessione: cancellati quando chiudi il browser
• Cookie persistenti: rimangono sul dispositivo per un periodo definito`,
						},
						{
							title: "2. Cookie Utilizzati",
							content: `Di seguito l'elenco dei cookie utilizzati sulla nostra piattaforma:`,
						},
						{
							title: "2.1 Cookie Tecnici (Necessari)",
							content: `Questi cookie sono essenziali per il funzionamento del sito e non possono essere disattivati. Non richiedono consenso.`,
							cookies: [
								{
									name: "session_token",
									purpose: "Autenticazione utente",
									duration: "7 giorni",
									type: "Prima parte",
								},
								{
									name: "language",
									purpose: "Preferenza lingua",
									duration: "1 anno",
									type: "Prima parte",
								},
								{
									name: "cookie_consent",
									purpose: "Memorizza preferenze cookie",
									duration: "6 mesi",
									type: "Prima parte",
								},
							],
						},
						{
							title: "2.2 Cookie Funzionali",
							content: `Questi cookie permettono funzionalità avanzate e personalizzazione. Richiedono consenso.`,
							cookies: [
								{
									name: "theme_preference",
									purpose: "Tema chiaro/scuro",
									duration: "1 anno",
									type: "Prima parte",
								},
								{
									name: "workout_cache",
									purpose: "Cache allenamenti offline (PWA)",
									duration: "Sessione",
									type: "Prima parte",
								},
							],
						},
						{
							title: "2.3 Cookie Analitici",
							content: `Questi cookie ci aiutano a capire come i visitatori interagiscono con il sito. Richiedono consenso esplicito.`,
							cookies: [],
							note: "Utilizziamo il tracker Metricool (be.js) per misurare gli accessi alla landing pubblica. Viene caricato solo dopo il consenso alla categoria Analytics. Eventuali cookie e tempi di conservazione sono determinati dal fornitore Metricool.",
						},
						{
							title: "2.4 Cookie di Profilazione/Marketing",
							content: `Questi cookie sono utilizzati per tracciare i visitatori e mostrare annunci pertinenti. Richiedono consenso esplicito.`,
							cookies: [],
							note: "Nota: Attualmente NON utilizziamo cookie di profilazione o marketing.",
						},
						{
							title: "3. Base Giuridica",
							content: `• Cookie tecnici: installati sulla base dell'art. 122, comma 1, del Codice Privacy (necessari per la fornitura del servizio richiesto)
• Cookie funzionali, analitici e di profilazione: installati solo previo consenso esplicito dell'utente (art. 6.1.a GDPR)

Il consenso è raccolto tramite il banner cookie alla prima visita. L'utente può:
• Accettare tutti i cookie
• Accettare solo i cookie necessari (chiudendo il banner con "X")
• Personalizzare le preferenze per categoria
• Rifiutare tutti i cookie non necessari`,
						},
						{
							title: "4. Come Gestire i Cookie",
							content: `Puoi gestire le tue preferenze cookie in qualsiasi momento:

a) Tramite il nostro sito:
   Clicca su "Gestisci Cookie" nel footer per modificare le tue preferenze.

b) Tramite il browser:
   Puoi configurare il browser per bloccare o eliminare i cookie.

   • Chrome: Impostazioni > Privacy e sicurezza > Cookie
   • Firefox: Opzioni > Privacy e sicurezza
   • Safari: Preferenze > Privacy
   • Edge: Impostazioni > Privacy, ricerca e servizi

c) Opt-out specifici:
  Puoi revocare il consenso Analytics dal link "Gestisci Cookie" nel footer.

Nota: disabilitare i cookie tecnici può compromettere il funzionamento del sito.`,
						},
						{
							title: "5. Rinnovo del Consenso",
							content: `In conformità alle Linee Guida del Garante, il consenso cookie viene richiesto nuovamente:

• Dopo 6 mesi dalla prima accettazione
• Se modifichiamo significativamente i cookie utilizzati
• Se cancelli i cookie dal browser

La tua scelta precedente non influenza la possibilità di modificare le preferenze in qualsiasi momento.`,
						},
						{
							title: "6. Trasferimento Dati",
							content: `Il tracker Metricool trasmette dati di navigazione al fornitore per misurare gli accessi alla landing pubblica. I paesi di trattamento e le garanzie applicabili a eventuali trasferimenti internazionali dipendono dal servizio e sono descritti nella documentazione privacy di Metricool.`,
						},
						{
							title: "7. Diritti dell'Utente",
							content: `Hai il diritto di:

• Revocare il consenso ai cookie in qualsiasi momento
• Richiedere informazioni sui cookie utilizzati
• Presentare reclamo al Garante per la Protezione dei Dati Personali

Per esercitare i tuoi diritti: privacy@kraveon.com`,
						},
						{
							title: "8. Aggiornamenti",
							content: `Questa Cookie Policy può essere aggiornata periodicamente. La data dell'ultimo aggiornamento è indicata in cima alla pagina. Ti invitiamo a consultarla regolarmente.`,
						},
						{
							title: "9. Contatti",
							content: `Per domande sulla Cookie Policy:
Email: privacy@kraveon.com

Garante per la Protezione dei Dati Personali:
www.garanteprivacy.it`,
						},
					],
				}
			: {
					title: "Cookie Policy",
					lastUpdate: "Last updated: October 2026",
					intro: `This Cookie Policy explains what cookies are, how we use them on Kraveon, and how to manage your preferences. This policy complies with EU Regulation 2016/679 (GDPR) and the ePrivacy Directive.`,
					sections: [
						{
							title: "1. What are Cookies",
							content: `Cookies are small text files stored on your device when you visit a website. They are widely used to make websites work more efficiently and provide information to site owners.

Cookies can be:
• First-party cookies: set directly by our site
• Third-party cookies: set by domains other than ours

By duration:
• Session cookies: deleted when you close the browser
• Persistent cookies: remain on the device for a defined period`,
						},
						{
							title: "2. Cookies Used",
							content: `Below is the list of cookies used on our platform:`,
						},
						{
							title: "2.1 Technical Cookies (Necessary)",
							content: `These cookies are essential for the site to function and cannot be disabled. They do not require consent.`,
							cookies: [
								{
									name: "session_token",
									purpose: "User authentication",
									duration: "7 days",
									type: "First party",
								},
								{
									name: "language",
									purpose: "Language preference",
									duration: "1 year",
									type: "First party",
								},
								{
									name: "cookie_consent",
									purpose: "Stores cookie preferences",
									duration: "6 months",
									type: "First party",
								},
							],
						},
						{
							title: "2.2 Functional Cookies",
							content: `These cookies enable advanced features and personalization. They require consent.`,
							cookies: [
								{
									name: "theme_preference",
									purpose: "Light/dark theme",
									duration: "1 year",
									type: "First party",
								},
								{
									name: "workout_cache",
									purpose: "Offline workout cache (PWA)",
									duration: "Session",
									type: "First party",
								},
							],
						},
						{
							title: "2.3 Analytics Cookies",
							content: `These cookies help us understand how visitors interact with the site. They require explicit consent.`,
							cookies: [],
							note: "We use the Metricool tracker (be.js) to measure visits to the public landing page. It is loaded only after consent to the Analytics category. Any cookies and retention periods are determined by Metricool.",
						},
						{
							title: "2.4 Profiling/Marketing Cookies",
							content: `These cookies are used to track visitors and show relevant ads. They require explicit consent.`,
							cookies: [],
							note: "Note: We currently do NOT use profiling or marketing cookies.",
						},
						{
							title: "3. Legal Basis",
							content: `• Technical cookies: installed based on necessity for the requested service
• Functional, analytics, and profiling cookies: installed only with explicit user consent (Art. 6.1.a GDPR)

Consent is collected via the cookie banner on first visit. The user can:
• Accept all cookies
• Accept only necessary cookies (closing banner with "X")
• Customize preferences by category
• Reject all non-necessary cookies`,
						},
						{
							title: "4. How to Manage Cookies",
							content: `You can manage your cookie preferences at any time:

a) Through our site:
   Click "Manage Cookies" in the footer to modify your preferences.

b) Through your browser:
   You can configure your browser to block or delete cookies.

   • Chrome: Settings > Privacy and security > Cookies
   • Firefox: Options > Privacy & Security
   • Safari: Preferences > Privacy
   • Edge: Settings > Privacy, search, and services

Note: disabling technical cookies may impair site functionality.`,
						},
						{
							title: "5. Consent Renewal",
							content: `Cookie consent is requested again:

• After 6 months from first acceptance
• If we significantly modify cookies used
• If you delete cookies from your browser

Your previous choice does not affect your ability to modify preferences at any time.`,
						},
						{
							title: "6. Data Transfer",
							content: `The Metricool tracker sends browsing data to the provider to measure visits to the public landing page. Processing locations and safeguards for any international transfers depend on the service and are described in Metricool's privacy documentation.`,
						},
						{
							title: "7. Your Rights",
							content: `You have the right to:

• Withdraw consent to cookies at any time
• Request information about cookies used
• File a complaint with the Data Protection Authority

To exercise your rights: privacy@kraveon.com`,
						},
						{
							title: "8. Updates",
							content: `This Cookie Policy may be updated periodically. The last update date is shown at the top. We encourage you to check it regularly.`,
						},
						{
							title: "9. Contact",
							content: `For questions about Cookie Policy:
Email: privacy@kraveon.com`,
						},
					],
				};

	return (
		<PublicLayout>
			<div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
				<div className="mb-8">
					<h1 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3">
						<Cookie className="w-8 h-8 text-volt" />
						{content.title}
					</h1>
					<p className="text-muted-foreground">{content.lastUpdate}</p>
				</div>

				<Card className="bg-card border-border rounded-sm">
					<CardContent className="p-6">
						<ScrollArea className="h-[600px] pr-4" viewportClassName="[&>div]:!block">
							<div className="space-y-8">
								<p className="text-sm text-muted-foreground leading-relaxed">
									{content.intro}
								</p>

								{content.sections.map((section, index) => (
									<div key={index}>
										<h2 className="font-heading text-xl text-volt mb-3">
											{section.title}
										</h2>
										<p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed mb-4">
											{section.content}
										</p>

										{section.cookies && section.cookies.length > 0 && (
											<div className="overflow-x-auto mb-4">
												<table className="w-full text-sm">
													<thead>
														<tr className="border-b border-border">
															<th className="text-left py-2 px-3 font-medium">
																Cookie
															</th>
															<th className="text-left py-2 px-3 font-medium">
																{language === "it" ? "Finalità" : "Purpose"}
															</th>
															<th className="text-left py-2 px-3 font-medium">
																{language === "it" ? "Durata" : "Duration"}
															</th>
															<th className="text-left py-2 px-3 font-medium">
																{language === "it" ? "Tipo" : "Type"}
															</th>
														</tr>
													</thead>
													<tbody>
														{section.cookies.map((cookie, i) => (
															<tr key={i} className="border-b border-border/50">
																<td className="py-2 px-3 font-mono text-xs text-volt">
																	{cookie.name}
																</td>
																<td className="py-2 px-3 text-muted-foreground">
																	{cookie.purpose}
																</td>
																<td className="py-2 px-3 text-muted-foreground">
																	{cookie.duration}
																</td>
																<td className="py-2 px-3">
																	<Badge variant="outline" className="text-xs">
																		{cookie.type}
																	</Badge>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}

										{section.note && (
											<p className="text-xs text-yellow-500 italic">
												{section.note}
											</p>
										)}
									</div>
								))}
							</div>
						</ScrollArea>
					</CardContent>
				</Card>
			</div>
		</PublicLayout>
	);
}
